import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';

import {
  calcIsolatedMarginAssigned,
  calcNewLiquidationPrice,
} from '@flipster/features/src/shared/calculator/adjust-margin';
import { InstrumentV2ForApiInstanceType } from '@flipster/features/src/shared/snapshot/utils';
import { OpenPosition } from '@flipster/r230522/original/position';
import { Price } from '@flipster/ui';
import { getQuoteCurrencyPrecision } from '@flipster/utils/constants/currency';
import Num from '@flipster/utils/num';

import { adjustMarginStateBunja } from '../state';
import styles from './inputForm.module.scss';

interface Props {
  tab: 'add' | 'remove';
  currentPosition: OpenPosition;
  instrument: InstrumentV2ForApiInstanceType;
}

const PriceInfo = ({ tab, currentPosition, instrument }: Props) => {
  const transPositionAddFunds = useTranslations('Position_add_funds.r250401');
  const { amountAtom } = useBunja(adjustMarginStateBunja);
  const amount = useAtomValue(amountAtom);

  const {
    liquidationPrice,
    avgEntryPrice,
    position,
    isolatedMarginInitial,
    isolatedMarginAdded,
    isolatedMarginRealizedFee,
  } = currentPosition;
  const { pricePrecision, maintMarginRate, quoteCurrency } = instrument;

  const isolatedMarginAssigned = calcIsolatedMarginAssigned(
    isolatedMarginInitial,
    isolatedMarginAdded,
    isolatedMarginRealizedFee,
  );

  const hasAmount = Num(amount || 0).isGreaterThan(0);

  return (
    <section className={styles.desc}>
      <div>
        <span>{transPositionAddFunds('Info_current_margin')} </span>
        <Price
          price={isolatedMarginAssigned}
          precision={getQuoteCurrencyPrecision(quoteCurrency)}
          toFixed
          fontType='num2'
          currency={quoteCurrency}
        />
      </div>
      <div>
        <span>{transPositionAddFunds('Info_current_liq_price')} </span>
        <Price price={liquidationPrice} precision={pricePrecision} toFixed fontType='num2' currency={quoteCurrency} />
      </div>
      {hasAmount && (
        <div>
          <span>{transPositionAddFunds('Info_new_liq_price')} </span>
          <Price
            price={calcNewLiquidationPrice(
              tab,
              isolatedMarginAssigned,
              amount,
              avgEntryPrice,
              position,
              maintMarginRate,
              pricePrecision,
            )}
            precision={pricePrecision}
            toFixed
            fontType='num2'
            currency={quoteCurrency}
            className={styles.yellow}
          />
        </div>
      )}
    </section>
  );
};

export default PriceInfo;
